import Button from "../../components/Button";
import { DataType, DataListType, IWidget, ACTION_VARIANT } from "../../types";
import { TrackingUtil } from "../../utils/tracking";
import { DOM } from "../../utils/domActions";

class VerticalCardList implements IWidget {
    container: HTMLElement | null = null;
    listData: DataListType | null = null;
    items: DataType[] = [];
    campaignId: string | null = null;
    
    render(): HTMLElement {
        if (!this.container) {
            this.container = DOM.createElement("div", {
                id: "trustshop-embed-vertical-card-list--container",
                className: "trustshop-embed-vertical-card-list--container trustshop-embed--card",
            });
        }

        // Add header if title present
        if (this.listData?.title) {
            const header = DOM.createElement("h2", {
                className: "trustshop-embed-vertical-card-list--title",
                textContent: this.listData.title,
            });
            this.container.appendChild(header);
        }

        // Create cards container
        const cardsContainer = DOM.createElement("div", {
            className: "trustshop-embed-vertical-card-list--cards",
        });

        this.items.forEach((data) => {
            const card = this.renderCard(data);
            cardsContainer.appendChild(card);
        });

        this.container.appendChild(cardsContainer);

        return this.container;
    }

    renderCard(data: DataType): HTMLElement {
        const card = DOM.createElement("div", {
            className: "trustshop-embed-vertical-card-list--card trustshop-embed--card",
        });

        // Top row with icon, title, and actions
        const topRow = DOM.createElement("div", {
            className: "trustshop-embed-vertical-card-list--card-top",
        });

        // Icon
        if (data.img_url) {
            const iconWrapper = DOM.createElement("div", {
                className: "trustshop-embed-vertical-card-list--card-icon",
            });

            const img = DOM.createElement("img", {
                attributes: {
                    src: data.img_url,
                    alt: data.title || "Icon",
                },
            });
            iconWrapper.appendChild(img);
            topRow.appendChild(iconWrapper);
        }

        // Title
        if (data.title) {
            const title = DOM.createElement("h3", {
                className: "trustshop-embed-vertical-card-list--card-title",
                textContent: data.title,
            });
            topRow.appendChild(title);
        }

        // Actions container
        if (data.primary_action || data.secondary_actions) {
            const actionsContainer = DOM.createElement("div", {
                className: "trustshop-embed-vertical-card-list--card-actions",
            });

            // Primary action
            if (data.primary_action) {
                const actionBtn = Button.create({
                    ...data.primary_action,
                    variant: data.primary_action.variant || ACTION_VARIANT.SECONDARY,
                    className: "trustshop-embed-vertical-card-list--card-action",
                });
                actionsContainer.appendChild(actionBtn);
            }

            // Secondary actions
            if (data.secondary_actions) {
                const secondaryActions = Array.isArray(data.secondary_actions) 
                    ? data.secondary_actions 
                    : [data.secondary_actions];

                secondaryActions.forEach((action) => {
                    const actionBtn = Button.create({
                        ...action,
                        variant: action.variant || ACTION_VARIANT.PLAIN,
                        className: "trustshop-embed-vertical-card-list--card-secondary",
                    });
                    actionsContainer.appendChild(actionBtn);
                });
            }

            topRow.appendChild(actionsContainer);
        }

        // Description 
        if (data.description) {
            const description = DOM.createElement("p", {
                className: "trustshop-embed-vertical-card-list--card-description",
                textContent: data.description,
            });
            topRow.appendChild(description);
        }

        card.appendChild(topRow);

        return card;
    }

    show(): void {
        if (this.container) {
            this.container.style.display = "block";
        }
    }

    hide(): void {
        if (this.container) {
            this.container.style.display = "none";
        }
    }

    destroy(): void {
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
    }

    isVisible(): boolean {
        return this.container ? this.container.style.display !== "none" : false;
    }

    toggle(): void {
        if (this.isVisible()) {
            this.hide();
        } else {
            this.show();
        }
    }

    updateData(data: DataListType | DataType | DataType[]): void {
        // Check if it's a DataListType with title, subtitle, and data array
        if (data && "data" in data && Array.isArray(data.data)) {
            // Store the full DataListType object
            this.listData = data as DataListType;
            this.items = data.data.filter(Boolean) as DataType[];
        } else if (Array.isArray(data)) {
            // Direct array of items
            this.items = data;
            this.listData = null;
        } else {
            // Single item, wrap in array
            this.items = [data as DataType];
            this.listData = null;
        }
    }
}

const VerticalCardListInstance = new VerticalCardList();

export default VerticalCardListInstance;